import { ElementType, forwardRef } from 'react';
import { Link, useNavigate } from '@tanstack/react-router';
import { Icon, IconName } from '@frontend/icons';
import { useNavSize } from '@frontend/responsiveness';
import { useLocationService } from '@frontend/wam-location-service';
import { theme } from '@frontend/theme';
import { Tray, useModalSingletonControl, useModalSingletonStore } from '@frontend/design-system';
import { useLayout } from '../../providers/layout.provider';
import { LocationDetailsTray } from './trays/location-details.tray';
import { LocationPickerTray } from './trays/location-picker.tray';
import { RecentlyVisitedTray } from './trays/recently-visited.tray';

const LOCATION_PICKER_ID = 'location-picker';
const LOCATION_DETAILS_ID = 'location-details';
const RECENTLY_VISITED_ID = 'recently-visited';

export const TopNav = () => {
  const { openNav } = useLayout();
  const { currentLocation } = useLocationService();
  const navigate = useNavigate();

  const { modalProps: recentlyVisitedModalProps, triggerProps: recentlyVisitedTriggerProps } =
    useModalSingletonControl(RECENTLY_VISITED_ID);
  const { modalProps: locationPickerModalProps, triggerProps: locationPickerTriggerProps } =
    useModalSingletonControl(LOCATION_PICKER_ID);
  const { modalProps: locationDetailsModalProps, triggerProps: locationDetailsTriggerProps } =
    useModalSingletonControl(LOCATION_DETAILS_ID);

  const modalSingletonStore = useModalSingletonStore(['getOpenModal']);
  const openModal = modalSingletonStore.getOpenModal();

  const navSize = useNavSize();
  const isSmallerThanDesktop = navSize.isLt('large');

  const onLocationSelection = () => {
    locationPickerModalProps.onClose();
    navigate({ to: '/dashboard' as any });
  };

  return (
    <>
      <header
        css={{
          display: 'flex',
          alignItems: 'stretch',
          justifyContent: 'space-between',
          background: theme.colors.neutral90,
          color: theme.colors.white,
          height: theme.spacing(8),
        }}
      >
        <TopNavMenu>
          {isSmallerThanDesktop && <TopNavMenuItem iconName='hamburger-menu' onClick={() => openNav()} />}
          <TopNavMenuItem to='/dashboard'>
            <Icon name='weave-logo' size={36} css={{ maxWidth: 'initial' }} />
            <span css={{ fontSize: theme.font.size.h2, fontWeight: theme.font.weight.bold }}>WAM</span>
          </TopNavMenuItem>
        </TopNavMenu>
        <TopNavMenu>
          <TopNavMenuItem
            iconName='history'
            active={openModal?.id === RECENTLY_VISITED_ID}
            {...recentlyVisitedTriggerProps}
          >
            Recently Visited
          </TopNavMenuItem>
          <TopNavMenuItem
            iconName='location'
            active={openModal?.id === LOCATION_PICKER_ID}
            {...locationPickerTriggerProps}
          >
            {currentLocation?.name || 'Select Location'}
          </TopNavMenuItem>
          <TopNavMenuItem
            iconName='building'
            active={openModal?.id === LOCATION_DETAILS_ID}
            {...locationDetailsTriggerProps}
          />
          <TopNavMenuItem iconName='x' to={'/logout'} />
        </TopNavMenu>
      </header>
      <Tray {...locationPickerModalProps} width='large' mountTarget='#tray-target'>
        <LocationPickerTray onClose={locationPickerModalProps.onClose} onLocationSelection={onLocationSelection} />
      </Tray>
      <Tray {...locationDetailsModalProps} width='large' mountTarget='#tray-target'>
        <LocationDetailsTray onClose={locationDetailsModalProps.onClose} />
      </Tray>
      <Tray {...recentlyVisitedModalProps} width='large' mountTarget='#tray-target'>
        <RecentlyVisitedTray onClose={recentlyVisitedModalProps.onClose} />
      </Tray>
    </>
  );
};

type TopNavMenuProps = {
  children: React.ReactNode;
};

const TopNavMenu = ({ children }: TopNavMenuProps) => (
  <ul css={{ display: 'flex', alignItems: 'stretch', listStyle: 'none', margin: 0, padding: 0 }}>{children}</ul>
);

type TopNavMenuItemProps = {
  active?: boolean;
  as?: ElementType;
  children?: React.ReactNode;
  iconName?: IconName;
  onClick?: () => void;
  to?: string;
};

const TopNavMenuItem = forwardRef<HTMLLIElement, TopNavMenuItemProps>(
  ({ active = false, as = 'li', children, iconName, onClick, to }, ref) => {
    const Component = as;
    const interactive = !!(onClick || to);

    return (
      <Component
        ref={ref}
        css={{
          display: 'flex',
          alignItems: 'center',
          cursor: interactive ? 'pointer' : 'default',
          whiteSpace: 'nowrap',
          padding: to ? 0 : theme.spacing(0, 2),
          color: active ? theme.colors.black : 'inherit',
          background: active ? theme.colors.white : 'inherit',
          transition: 'background 150ms ease-out, color 150ms ease-out, border-color 150ms ease-out',
          '&:hover':
            interactive && !active
              ? {
                  background: theme.colors.primary50,
                }
              : null,
        }}
        onClick={onClick}
        onKeyDown={
          onClick
            ? (event: React.KeyboardEvent) => {
                if (interactive && (event.key === 'Enter' || event.key === ' ')) {
                  event.preventDefault();
                  onClick?.();
                }
              }
            : undefined
        }
        tabIndex={onClick ? 0 : undefined}
      >
        <HeaderLink to={to}>
          {children}
          {iconName && (
            <Icon name={iconName} css={{ maxWidth: 'initial', marginLeft: children ? theme.spacing(1) : 0 }} />
          )}
        </HeaderLink>
      </Component>
    );
  }
);

TopNavMenuItem.displayName = 'TopNavMenuItem';

type HeaderLinkProps = {
  to?: string;
  children?: React.ReactNode;
};

const HeaderLink = ({ to, children }: HeaderLinkProps) => {
  return to ? (
    <Link
      to={to}
      tabIndex={to ? 0 : undefined}
      css={{
        display: 'flex',
        alignItems: 'center',
        color: theme.colors.white,
        textDecoration: 'none',
        padding: theme.spacing(0, 2),
      }}
    >
      {children}
    </Link>
  ) : (
    children
  );
};
